import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import { brandColors } from '../../theme';
import LikeButton from './LikeButton';
import { useLikeIdea, useMarkIdeaDealtWith } from '../../hooks/useIdeaBoard';
import type { Idea } from '../../types/ideaBoard';

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30)
    return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  if (diffDays < 365)
    return `${Math.floor(diffDays / 30)} month${Math.floor(diffDays / 30) > 1 ? 's' : ''} ago`;
  return `${Math.floor(diffDays / 365)} year${Math.floor(diffDays / 365) > 1 ? 's' : ''} ago`;
}

interface IdeaCardProps {
  idea: Idea;
  isAdmin?: boolean;
}

const IdeaCard: React.FC<IdeaCardProps> = ({ idea, isAdmin = false }) => {
  const [dealtWithOpen, setDealtWithOpen] = useState(false);
  const [featureUpdateSummary, setFeatureUpdateSummary] = useState('');

  const likeMutation = useLikeIdea();
  const markDealtWithMutation = useMarkIdeaDealtWith();

  const handleLike = () => {
    if (!idea.userHasLiked) {
      likeMutation.mutate(idea.id);
    }
  };

  const handleOpenDealtWith = () => {
    setFeatureUpdateSummary('');
    setDealtWithOpen(true);
  };

  const handleCloseDealtWith = () => {
    setDealtWithOpen(false);
  };

  const handleConfirmDealtWith = async () => {
    markDealtWithMutation.mutate(
      {
        ideaId: idea.id,
        featureUpdateSummary: featureUpdateSummary.trim() || undefined,
      },
      {
        onSuccess: () => {
          setDealtWithOpen(false);
        },
      }
    );
  };

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          mb: 1.5,
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderColor: brandColors.blue,
          },
        }}
      >
        <CardContent sx={{ pb: '12px !important' }}>
          <Typography variant="body1" sx={{ color: brandColors.darkGray, mb: 1.5, lineHeight: 1.6 }}>
            {idea.rewrittenEnglishSummary}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={idea.category}
                size="small"
                sx={{
                  backgroundColor: `${brandColors.blue}15`,
                  color: brandColors.blue,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                }}
              />
              <Typography variant="caption" sx={{ color: brandColors.mediumGray }}>
                {formatRelativeTime(idea.createdAt)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isAdmin && (
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={handleOpenDealtWith}
                  disabled={markDealtWithMutation.isPending}
                  sx={{ fontSize: '0.75rem', py: 0.25, textTransform: 'none' }}
                >
                  Mark as dealt with
                </Button>
              )}
              <LikeButton
                likeCount={idea.likeCount}
                userHasLiked={idea.userHasLiked}
                isLoading={likeMutation.isPending}
                onLike={handleLike}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Deal with confirmation dialog */}
      <Dialog open={dealtWithOpen} onClose={handleCloseDealtWith} maxWidth="sm" fullWidth aria-labelledby="deal-with-dialog-title">
        <DialogTitle id="deal-with-dialog-title">Mark Idea as Dealt With</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to mark this idea as dealt with? It will be moved to the archive.
          </DialogContentText>
          <DialogContentText sx={{ mb: 1, fontSize: '0.875rem', fontStyle: 'italic', color: brandColors.mediumGray }}>
            "{idea.rewrittenEnglishSummary}"
          </DialogContentText>
          <TextField
            label="Feature Update Summary (optional)"
            placeholder="Describe how this was addressed or what feature was added..."
            multiline
            rows={3}
            fullWidth
            value={featureUpdateSummary}
            onChange={(e) => setFeatureUpdateSummary(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText={`${featureUpdateSummary.length}/2000 — Leave blank to skip creating a feature update`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDealtWith} disabled={markDealtWithMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDealtWith}
            variant="contained"
            color="success"
            disabled={markDealtWithMutation.isPending}
            startIcon={markDealtWithMutation.isPending ? <CircularProgress size={16} /> : null}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IdeaCard;
